/**
 * GOLDEN SLICE (ARCH-7): the ONLY way components read the exercise library.
 *
 * Narrow hooks so a component re-renders only when its slice changes. `useShallow` on anything
 * that returns a new array/object per call. Non-React code (e.g. src/lib/workouts/serialize.ts)
 * reads with `useExerciseLibraryStore.getState()` instead.
 */

import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Exercise } from '@/types/exercises';
import { useExerciseLibraryStore, type ExerciseLibraryStore } from './exerciseLibraryStore';

/** Pure selector: the library in display order. Exported for getState() callers and tests. */
export const selectExercises = (s: ExerciseLibraryStore): Exercise[] => s.order.map((id) => s.byId[id]).filter(Boolean);

/** The library in display order. Doesn't trigger a load; pair with `useExerciseLibrary` where you need one. */
export const useExerciseList = (): Exercise[] => useExerciseLibraryStore(useShallow(selectExercises));

export const useExerciseById = (id: string | null | undefined): Exercise | undefined =>
  useExerciseLibraryStore((s) => (id ? s.byId[id] : undefined));

export const useExerciseLibraryStatus = () =>
  useExerciseLibraryStore(useShallow((s) => ({ status: s.status, error: s.error })));

/** The write in flight for this row, if any (disable its actions while set). */
export const useExercisePendingOp = (id: string) => useExerciseLibraryStore((s) => s.pending[id]);

export const useExerciseLibraryActions = () =>
  useExerciseLibraryStore(
    useShallow((s) => ({
      load: s.load,
      loadDetail: s.loadDetail,
      save: s.save,
      remove: s.remove,
      duplicate: s.duplicate,
    })),
  );

/**
 * The common case: the list + status, loading it on first mount if nobody has yet.
 * Safe to use in many components at once (load() de-duplicates).
 */
export function useExerciseLibrary() {
  const exercises = useExerciseList();
  const { status, error } = useExerciseLibraryStatus();
  const load = useExerciseLibraryStore((s) => s.load);

  useEffect(() => {
    void load();
  }, [load]);

  return { exercises, status, error, isLoading: status === 'idle' || status === 'loading' };
}
